import { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Alert,
  Typography,
  Paper,
  Box,
  CircularProgress,
  Fade,
  Slide,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { fetchUserPermissions } from "../utils/fetchUserPermissions";
import EmailIcon from "@mui/icons-material/Email";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RobotCaptcha from "react-angle-captcha";
import "react-angle-captcha/style.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function LoginForm() {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const navigate = useNavigate();
  const [captchaVerified, setCaptchaVerified] = useState(false);
  // Check for token and redirect to dashboard if present
  useEffect(() => {
    const checkSession = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/me`, {
          method: "GET",
          credentials: "include",
        });
        if (res.ok) {
          navigate("/dashboard", { replace: true });
        }
      } catch (err) {
        console.log("No active session");
      }
    };

    checkSession();
  }, [navigate]);

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (captchaVerified == false) {
        setError("Captcha verification failed");
      } else {
        const res = await fetch(`${BASE_URL}/api/auth/request-otp`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, bo_id: "BFXIND" }),
          credentials: "include",
        });

        const data = await res.json();

        if (res.ok) {
          setOtpSent(true);
          setError("");
        } else {
          setError(data.message || "Failed to send OTP");
        }
      }
    } catch (err) {
      setError("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/api/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp, bo_id: "BFXIND" }),
        credentials: "include",
      });

      const data = await res.json();

      if (res.ok) {
        // No need to store tokens - using HttpOnly cookies
        // Fetch permissions and navigate to dashboard
        const permissions = await fetchUserPermissions();
        navigate("/dashboard", { replace: true });
      } else {
        setError(data.message || "Invalid OTP");
      }
    } catch (err) {
      setError("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setOtpSent(false);
    setOtp("");
    setError("");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        maxHeight: "100vh",
        width: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        position: "fixed",
        top: 0,
        left: 0,
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: "-50%",
          right: "-50%",
          width: "100%",
          height: "100%",
          background:
            "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
          animation: "float 20s ease-in-out infinite",
        },
        "@keyframes float": {
          "0%, 100%": { transform: "translate(0, 0)" },
          "50%": { transform: "translate(-20px, -20px)" },
        },
      }}
    >
      <Fade in timeout={800}>
        <Paper
          elevation={24}
          sx={{
            p: { xs: 3, sm: 4, md: 5 },
            width: { xs: "90%", sm: 400, md: 420 },
            maxWidth: "95vw",
            maxHeight: { xs: "90vh", sm: "85vh" },
            overflowY: "auto",
            borderRadius: 4,
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
            position: "relative",
            zIndex: 1,
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(102, 126, 234, 0.3)",
              borderRadius: "3px",
            },
          }}
        >
          {/* Logo/Brand Section */}
          <Box sx={{ textAlign: "center", mb: { xs: 3, sm: 4 } }}>
            <Box
              sx={{
                width: { xs: 80, sm: 100, md: 120 },
                height: { xs: 80, sm: 100, md: 120 },
                margin: "0 auto",
                mb: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/BFxLoginLogo.svg`}
                alt="BFx Logo"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
            <Typography
              variant="h4"
              fontWeight="700"
              sx={{
                fontSize: { xs: "1.75rem", sm: "2rem", md: "2.125rem" },
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 1,
              }}
            >
              BloomPeople
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: { xs: "0.875rem", sm: "0.875rem" } }}
            >
              {otpSent ? "Verify your identity" : "Employee Management System"}
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Fade in>
              <Alert
                severity="error"
                sx={{
                  mb: { xs: 2, sm: 3 },
                  borderRadius: 2,
                  fontSize: { xs: "0.875rem", sm: "0.875rem" },
                  "& .MuiAlert-icon": {
                    fontSize: { xs: 20, sm: 24 },
                  },
                }}
              >
                {error}
              </Alert>
            </Fade>
          )}

          {/* Email Form */}
          {!otpSent ? (
            <Slide direction="right" in={!otpSent} timeout={500}>
              <form onSubmit={handleRequestOtp}>
                <TextField
                  label="Email Address"
                  type="email"
                  fullWidth
                  margin="normal"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  InputProps={{
                    startAdornment: (
                      <EmailIcon
                        sx={{
                          color: "action.active",
                          mr: 1,
                          fontSize: { xs: 20, sm: 24 },
                        }}
                      />
                    ),
                  }}
                  sx={{
                    mb: { xs: 2, sm: 3 },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover fieldset": {
                        borderColor: "#667eea",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#667eea",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                    },
                  }}
                />
                <div>
                  <RobotCaptcha
                    onChange={(verified) => {
                      setCaptchaVerified(verified);
                    }}
                    hideRefresh={true}
                  />
                </div>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  sx={{
                    height: { xs: 48, sm: 52, md: 56 },
                    borderRadius: 2,
                    fontSize: { xs: 14, sm: 15, md: 16 },
                    fontWeight: 600,
                    textTransform: "none",
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                      boxShadow: "0 6px 20px rgba(102, 126, 234, 0.6)",
                      transform: "translateY(-2px)",
                    },
                    "&:disabled": {
                      background: "linear-gradient(135deg, #ccc 0%, #999 100%)",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "white" }} />
                  ) : (
                    "Send OTP"
                  )}
                </Button>
              </form>
            </Slide>
          ) : (
            <Slide direction="left" in={otpSent} timeout={500}>
              <form onSubmit={handleVerifyOtp}>
                {/* Email Display */}
                <Box
                  sx={{
                    mb: { xs: 2, sm: 3 },
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: 2,
                    background:
                      "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
                    border: "1px solid rgba(102, 126, 234, 0.2)",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                  >
                    OTP sent to
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="600"
                    color="primary"
                    sx={{
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                      wordBreak: "break-all",
                    }}
                  >
                    {email}
                  </Typography>
                </Box>

                {/* OTP Input */}
                <TextField
                  label="Enter OTP"
                  type="text"
                  fullWidth
                  margin="normal"
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 6) {
                      setOtp(value);
                    }
                  }}
                  required
                  inputProps={{
                    maxLength: 6,
                    pattern: "[0-9]{6}",
                    inputMode: "numeric",
                  }}
                  helperText="Enter the 6-digit code"
                  sx={{
                    mb: { xs: 2, sm: 3 },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover fieldset": {
                        borderColor: "#667eea",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#667eea",
                      },
                    },
                  }}
                />

                {/* Verify Button */}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading || otp.length !== 6}
                  sx={{
                    height: { xs: 48, sm: 52, md: 56 },
                    borderRadius: 2,
                    fontSize: { xs: 14, sm: 15, md: 16 },
                    fontWeight: 600,
                    textTransform: "none",
                    mb: { xs: 1.5, sm: 2 },
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                      boxShadow: "0 6px 20px rgba(102, 126, 234, 0.6)",
                      transform: "translateY(-2px)",
                    },
                    "&:disabled": {
                      background: "linear-gradient(135deg, #ccc 0%, #999 100%)",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "white" }} />
                  ) : (
                    "Verify OTP"
                  )}
                </Button>

                {/* Back Button */}
                <Button
                  variant="text"
                  fullWidth
                  onClick={handleBackToEmail}
                  disabled={loading}
                  startIcon={
                    <ArrowBackIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                  }
                  sx={{
                    height: { xs: 44, sm: 48 },
                    borderRadius: 2,
                    fontSize: { xs: 13, sm: 14, md: 15 },
                    textTransform: "none",
                    color: "#667eea",
                    fontWeight: 600,
                    "&:hover": {
                      background: "rgba(102, 126, 234, 0.1)",
                    },
                  }}
                >
                  Back to Email
                </Button>
              </form>
            </Slide>
          )}

          {/* Footer */}
          <Box sx={{ mt: { xs: 3, sm: 4 }, textAlign: "center" }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
            >
              Secured by OTP Authentication
            </Typography>
          </Box>
        </Paper>
      </Fade>
    </Box>
  );
}
